/**
 * Lobby Created Node - Player View
 *
 * Shows the lobby code prominently and tracks which players
 * have confirmed they've joined. Similar to ready_up in
 * confirmation flow but with a lobby code access-card feel.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { LobbyCodeDisplay } from "../../components/LobbyCodeDisplay";
import { LobbyCredentialsCard } from "../../components/LobbyCredentialsCard";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type LobbyField = {
  key: string;
  label: string;
  value: string;
  type: string;
};

type LobbyCreatedState = {
  confirmedPlayers: string[];
  lobbyCode: string | null;
  lobbyFields?: LobbyField[];
};

export function LobbyCreatedView({
  nodeState,
  players,
  onAction,
}: NodePlayerViewProps<LobbyCreatedState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as LobbyCreatedState;
  const config = nodeState.config as { labels?: { title?: string; subtitle?: string } };
  const title = config.labels?.title ?? "Lobby Ready";
  const subtitle = config.labels?.subtitle ?? "Join the game lobby and confirm below";
  const hasConfirmed = state.confirmedPlayers.includes(userId || "");
  const confirmedCount = state.confirmedPlayers.length;
  const totalPlayers = players.length;
  const progress = totalPlayers > 0 ? confirmedCount / totalPlayers : 0;

  // Lobby field details from upstream lobby_code_input, with fallback for legacy state
  const lobbyFields: LobbyField[] =
    state.lobbyFields && state.lobbyFields.length > 0
      ? state.lobbyFields
      : state.lobbyCode
        ? [{ key: "lobbyCode", label: "Lobby Code", value: state.lobbyCode, type: "text" }]
        : [];
  const isSingleTextField = lobbyFields.length === 1 && lobbyFields[0].type === "text";

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);

    const result = await onAction("confirm", { userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* Lobby details display */}
      {lobbyFields.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 25, delay: 0.1 }}
          className="flex justify-center mb-6"
        >
          {isSingleTextField ? (
            <LobbyCodeDisplay code={lobbyFields[0].value} label={lobbyFields[0].label} />
          ) : (
            <LobbyCredentialsCard
              fields={lobbyFields.map((f) => ({
                key: f.key,
                label: f.label,
                value: f.value,
                type: f.type === "password" ? "password" : "text",
              }))}
            />
          )}
        </motion.div>
      )}

      {/* Confirmation action — button morphs into confirmed pill in place */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="flex justify-center mb-6"
      >
        <AnimatePresence mode="wait">
          {!hasConfirmed ? (
            <motion.button
              key="confirm-btn"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleConfirm}
              disabled={isSubmitting}
              className={cn(
                "relative px-8 py-3 rounded-sm font-bold text-sm uppercase tracking-wider",
                "bg-brand-500 hover:bg-brand-600 text-surface-900",
                "shadow-[0_0_20px_rgba(38,217,181,0.2)]",
                "hover:shadow-[0_0_30px_rgba(38,217,181,0.3)]",
                "transition-all duration-200 cursor-pointer",
                "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
              )}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-surface-900/30 border-t-surface-900 rounded-full animate-spin" />
                  Confirming...
                </span>
              ) : (
                "I've Joined the Lobby"
              )}
            </motion.button>
          ) : (
            <motion.div
              key="confirmed"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-brand-500/30 bg-brand-500/[0.08]"
            >
              <Check size={14} strokeWidth={3} className="text-brand-400" aria-hidden="true" />
              <span className="font-bold text-sm uppercase tracking-wider text-brand-400">
                You're confirmed
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Progress counter */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="flex justify-center"
      >
        <div className="inline-flex flex-col items-stretch overflow-hidden rounded-lg bg-surface-800/80 border border-surface-700/50">
          <div className="flex items-center gap-3 px-4 py-2">
            <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
              {confirmedCount}
            </span>
            <span className="text-surface-500 text-sm">/</span>
            <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
              {totalPlayers}
            </span>
            <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
              confirmed
            </span>
          </div>

          {/* Progress bar — attached to bottom of pill */}
          <div className="h-1 bg-surface-900/60 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-brand-500 to-brand-400"
              initial={{ width: 0 }}
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>
      </motion.div>
    </NodeCard>
  );
}
